import { type FC, useMemo } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import { formatDistanceStrict } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { useUnit } from 'effector-react'

import { DataTable } from '@/components/ui/table/data-table'
import { Tag } from '@/components/ui/tag'
import { ThreeDotsLoader } from '@/components/ui/three-dots'
import { Tooltip } from '@/components/ui/tooltip'
import { TeamAvatar } from '@/modules/team/ui/team-avatar'
import { useFunnelChecksList } from '@/modules/tracking/funnel/hooks/use-funnel-checks-list.ts'
import { $funnelCheckInProgressIds } from '@/modules/tracking/funnel/models.ts'

import { type FunnelCheck, FunnelStatus } from '../../types'
import { getFunnelCheckStatus } from '../../utils'
import { FunnelTrackingTableActions } from './FunnelTrackingTableActions'

interface Props {}

const accountColumn: ColumnDef<FunnelCheck> = {
  header: 'Account',
  accessorKey: 'account',
  sortingFn: (a, b) => {
    return a.original.entryPointData.localeCompare(b.original.entryPointData)
  },
  cell: ({ row }) => {
    const account = row.original.entryPointData
    const type = row.original.entryPointType

    return (
      <div className="flex items-center gap-2.5">
        <TeamAvatar
          size={32}
          avatarSrc={row.original.avatarUrl}
          name={account}
          key={row.original.id}
        />
        <div className="flex flex-col gap-1">
          <span className="text-secondary-500 dark:text-secondary-100 text-sm">
            @{account}
          </span>
          <span className="text-secondary-300 text-xs">{type}</span>
        </div>
      </div>
    )
  },
}

export const FunnelTrackingTable: FC<Props> = () => {
  const { data } = useFunnelChecksList()
  const checkInProgressIds = useUnit($funnelCheckInProgressIds)

  const columns: ColumnDef<FunnelCheck>[] = useMemo(
    () => [
      accountColumn,
      {
        header: 'Goal URL',
        enableSorting: true,
        accessorKey: 'goalUrl',
        sortingFn: (a, b) => {
          return a.original.goalUrl.localeCompare(b.original.goalUrl)
        },
        cell: ({ row }) => {
          const urlWithoutHttps = row.original.goalUrl.replace('https://', '')

          return (
            <span className="text-secondary-500 dark:text-secondary-100 text-sm">
              {urlWithoutHttps}
            </span>
          )
        },
      },
      {
        header: 'Status',
        cell: ({ row }) => {
          const isChecking = checkInProgressIds.includes(row.original.id)
          const variant = isChecking
            ? 'neutral'
            : getFunnelCheckStatus(row.original.status)
          const zonedDate = toZonedTime(new Date(), 'UTC')
          const lastCheckDistance = formatDistanceStrict(
            new Date(row.original.lastCheckedAt),
            zonedDate,
            {
              roundingMethod: 'floor',
            },
          )
          const tooltipContent =
            row.original.status === FunnelStatus.Successful
              ? `${lastCheckDistance} ago`
              : row.original.failInfo

          return (
            <span className="inline-block">
              <Tooltip content={tooltipContent}>
                <Tag
                  variant={variant}
                  dot={variant}
                  withDot={!isChecking}
                  className="items-center capitalize"
                >
                  {isChecking ? (
                    <>
                      Checking <ThreeDotsLoader />
                    </>
                  ) : (
                    row.original.status.toLowerCase()
                  )}
                </Tag>
              </Tooltip>
            </span>
          )
        },
        enableSorting: true,
        accessorKey: 'sorting',
        sortingFn: (a, b) => {
          return a.original.status.localeCompare(b.original.status)
        },
      },
      {
        id: 'action',
        header: () => {
          return <span className="ml-auto self-end">Actions</span>
        },
        cell: ({ row }) => {
          return <FunnelTrackingTableActions row={row} />
        },
      },
    ],
    [checkInProgressIds],
  )

  return (
    <DataTable
      enablePagination
      enableSorting
      columns={columns}
      data={data!}
    />
  )
}
